import { useEffect } from "react";
import { Breadcrumb, Container } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";

import { EnvelopeAssignmentDetailHeader, Loading } from "components";
import IconArrowBack from "components/Icons/IconArrowBack/IconArrowBack";
import { AssignmentDetailPractice } from "features";

import { useAppSelector } from "store/hooks";
import { getRedEnvelopeParams, getOrangeEnvelopeParams, getGreenEnvelopeParams } from "store/slices/queryParams";

import { useGetUserThemeByIdQuery } from "api/generated";

import { useUserAssignmentDetail } from "utils/hooks/useUserAssignmentDetail";
import { processApiError } from "utils/processApiError";

const EnvelopeAssignmentDetail = () => {
  const { t } = useTranslation(["common"]);
  const { envelopeId, userThemeId, userAssignmentId } = useParams();
  const navigate = useNavigate();
  const redEnvelopeParams = useAppSelector(getRedEnvelopeParams(userThemeId));
  const orangeEnvelopeParams = useAppSelector(getOrangeEnvelopeParams(userThemeId));
  const greenEnvelopeParams = useAppSelector(getGreenEnvelopeParams(userThemeId));

  const { assignmentData, isLoading, isUnavailable, title } = useUserAssignmentDetail(userAssignmentId);
  const {
    data: themeData,
    isLoading: isThemeLoading,
    error: apiThemeError,
  } = useGetUserThemeByIdQuery({ userThemeId: userThemeId ?? "" }, { skip: !userThemeId });

  useEffect(() => {
    if (apiThemeError === undefined) return;

    processApiError(apiThemeError);
  }, [apiThemeError]);

  // AIDEV-NOTE: Kontrola parametrů musí být pod všemi hooky, jinak se při změně parametrů změní jejich počet. [TD-487]
  if (!envelopeId || !userThemeId || !userAssignmentId) return <Navigate to="/" replace />;

  const navigateAssignment = (newAssignmentId: string) => {
    navigate(`/ulohy/obalka/${envelopeId}/${userThemeId}/${newAssignmentId}`);
  };

  const generateQueryString = (params: Record<string, string>) => {
    let urlSlug = "";

    for (const [paramName, paramValue] of Object.entries(params)) {
      urlSlug += urlSlug.length === 0 ? "?" : "&";

      urlSlug += `${paramName}=${paramValue}`;
    }

    return urlSlug;
  };

  const getSearchValue = () => {
    switch (envelopeId) {
      case "cervena":
        return generateQueryString(redEnvelopeParams ?? {});
      case "oranzova":
        return generateQueryString(orangeEnvelopeParams ?? {});
      case "zelena":
        return generateQueryString(greenEnvelopeParams ?? {});
      default:
        return null;
    }
  };

  const returnBackQueryString = getSearchValue();

  return (
    <>
      <div className={"page-header  page-header--has-tabs"}>
        <Container className={"container-mw-md"}>
          <Breadcrumb>
            <li className={"breadcrumb-item"}>
              <Link
                to={{
                  pathname: `/ulohy/obalka/${envelopeId}/${userThemeId}`,
                  ...(returnBackQueryString === null ? null : { search: returnBackQueryString }),
                }}
              >
                <IconArrowBack />
                {t("back", { ns: "common" })}
              </Link>
            </li>
          </Breadcrumb>

          <EnvelopeAssignmentDetailHeader
            assignmentId={assignmentData?.assignment?.id ?? ""}
            userAssignmentId={userAssignmentId}
            themeId={themeData?.userTheme.theme?.id ?? ""}
            envelopeId={envelopeId}
            onNavigate={navigateAssignment}
            title={title}
          />
        </Container>
      </div>

      {isLoading || isThemeLoading ? (
        <Loading />
      ) : (
        <AssignmentDetailPractice
          assignment={assignmentData}
          userAssignmentId={userAssignmentId}
          isUnavailable={isUnavailable}
        />
      )}
    </>
  );
};

export default EnvelopeAssignmentDetail;
